import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnInit,
  SecurityContext,
  SimpleChanges,
  inject
} from '@angular/core';
import {DomSanitizer} from "@angular/platform-browser";
import { NgfAutolinkedTextComponent } from '../ngf-autolinked-text/ngf-autolinked-text.component';
import Autolinker from 'autolinker';

@Component({
    selector: 'ngf-rich-text-viewer',
    templateUrl: './ngf-rich-text-viewer.component.html',
    styleUrl: './ngf-rich-text-viewer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NgfRichTextViewerComponent implements OnInit, OnChanges{

  @Input()
  content: string = '';

  /**
   * 
   * @param textContent: string content to be sent to the quill viewer. 
   * It is important that this is already run through the sanitizer before calling this method.
   * @returns autolinked text -- link text are converted to anchor tags.
   */
  static autolink(textContent: string): string {
    const config = {...NgfAutolinkedTextComponent.AutolinkerConfig, email: true };

    // Apply autolinker to convert URLs to links
        return Autolinker.link(textContent, config);
  }

  private sanitizer: DomSanitizer = inject(DomSanitizer)

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['content']) {
        this.content = this.postProcessing(this.content);
      }
  }

  ngOnInit(): void {
      this.content = this.postProcessing(this.content);
  }

  private postProcessing(htmlContent: string): string {
    if (!htmlContent) return '';

    /**
     * quill 2.0.x getSemanticHTML is actually using inline styles now when generating HTML output,
     * which is nice, but running it through the sanitizer removes those styles and we lose highlights. 
     * This replaces those inline styles with the quill-compatible CSS class so that highlights are preserved after sanitization.
     * Importantly, we only do this on the viewer side, so as to keep stored rich text agnostic of quill-specific classes.
     */
    const highlightedText = htmlContent.replace(/style="background-color: (?:yellow|#FFFF00);"/gi, 'class="ql-bg-yellow"');

    // extra step to ensure any user generated content is sanitized before being rendered in the viewer
    const sanitizedContent = this.sanitizer.sanitize(SecurityContext.HTML, highlightedText || '') || '';

    // additional post processing to add CSS classes to list items based on their content type, and to autolink any URLs in the content
    const processedContent = this.processListItems(sanitizedContent);

    // finally, autolink any URLs in the content
    const autolinkedContent = NgfRichTextViewerComponent.autolink(processedContent);

    return autolinkedContent;
  }

  /**
   * Processes the HTML content to add CSS classes to list items based on their content type
   * Nested lists are handled by checking if a list item has direct text content, nested list content, or both, and adding appropriate classes for styling purposes.
   */
  private processListItems(htmlContent: string): string {
    if (!htmlContent || !htmlContent.trim()) {
      return htmlContent;
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Find all li elements
    const listItems = tempDiv.querySelectorAll('li');
    
    listItems.forEach(li => {
      const hasTextContent = this.hasDirectTextContent(li);
      const hasNestedList = li.querySelector(':scope > ol, :scope > ul') !== null;
      
      // Add appropriate classes based on content type
      if (hasTextContent && hasNestedList) {
        li.classList.add('item-with-text-and-list');
      } else if (hasTextContent && !hasNestedList) {
        li.classList.add('item-with-text-only');
      } else if (!hasTextContent && hasNestedList) {
        li.classList.add('item-with-list-only');
      }
    });

    return tempDiv.innerHTML;
  }

  /**
   * Checks if a list item has direct text content (not just whitespace)
   */
  private hasDirectTextContent(element: Element): boolean {
    for (const node of Array.from(element.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
        return true;
      }
      // Also check for text content in direct child elements that aren't lists
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (!['OL', 'UL'].includes(el.tagName) && el.textContent?.trim()) {
          return true;
        }
      }
    }
    return false;
  }
}
